import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    pictureUrls,
    prioritizeFile,
    readyPaint,
    subscribeDecoded,
    subscribeNetworkTier,
    viewerTargetWidth,
} from '../picture';
import DeletedImagePlaceholder, { isDeletedImageUrl } from '../../DeletedImagePlaceholder';

/** After this long with nothing decoded, the open picture paints its grid file directly. */
const STUCK_MS = 4000;

function layerStyle(url, fit, position) {
    return {
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(${JSON.stringify(url)})`,
        backgroundSize: fit,
        backgroundPosition: position,
        backgroundRepeat: 'no-repeat',
    };
}

/**
 * The open picture. The grid-sized file stays underneath and the sharp file
 * is painted over it only after it has decoded. A different file never keeps
 * the previous picture on screen.
 */
export default function HeldPicture({
    file,
    alt = '',
    fit = 'contain',
    position = 'center',
    className = '',
    style,
    priority = false,
}) {
    const [, setTier] = useState(null);
    useEffect(() => subscribeNetworkTier(setTier), []);
    const target = viewerTargetWidth();
    const urls = pictureUrls(file, target);
    const sharpId = urls.sharp || urls.low || '';
    const requestedRef = useRef('');
    const ready = readyPaint(file, target);
    const paintId = urls.low || urls.sharp || '';
    // Only a decoded file is painted. An undecoded background can flash at its
    // intrinsic size before cover/contain applies. A new file never keeps the
    // previous picture: that linger reads as the wrong slide on a slow network.
    const [committed, setCommitted] = useState(() => ready);
    const [committedId, setCommittedId] = useState(() => (ready ? paintId : ''));

    if (ready && committedId !== paintId) {
        setCommitted(ready);
        setCommittedId(paintId);
    } else if (ready && (
        !committed
        || committed.low !== ready.low
        || committed.sharp !== ready.sharp
    )) {
        setCommitted(ready);
    }

    // Ask once per file while open. A later repaint must not restart this
    // download or cancel the slides queued ahead of it. Leaving the slide
    // clears the mark so coming back asks again.
    if (!priority) {
        requestedRef.current = '';
    } else if (sharpId && requestedRef.current !== sharpId) {
        requestedRef.current = sharpId;
        prioritizeFile(file, target);
    }

    useEffect(() => subscribeDecoded(() => {
        const next = readyPaint(file, viewerTargetWidth());
        if (!next) return;
        setCommitted(next);
        setCommittedId(paintId);
    }), [file, paintId]);

    let shown = committedId === paintId ? (ready || committed) : null;
    const painted = Boolean(shown?.low || shown?.sharp);
    const [stuckId, setStuckId] = useState('');
    useEffect(() => {
        if (!priority || painted || !paintId) return undefined;
        const timer = setTimeout(() => setStuckId(paintId), STUCK_MS);
        return () => clearTimeout(timer);
    }, [priority, painted, paintId]);

    const [failedId, setFailedId] = useState('');
    const removed = file?.isDeleted || file?.isMissing || isDeletedImageUrl(file?.url?.original);
    const { objectFit, objectPosition, ...box } = style || {};

    if (removed || (failedId && failedId === paintId)) {
        return (
            <DeletedImagePlaceholder
                thumbnailUrl={file?.thumbnailUrl || (isDeletedImageUrl(file?.url?.original) ? file.url.original : null)}
                deletedAt={file?.deletedAt || null}
                label={removed ? undefined : 'Image unavailable'}
                width={file?.dimensions?.width || null}
                height={file?.dimensions?.height || null}
                className={className}
                style={{ maxWidth: '100%', maxHeight: '100%', margin: 'auto', ...box }}
            />
        );
    }

    let probe = null;
    if (!painted) {
        // The queue has not managed it; let the browser fetch the grid file itself.
        if (!priority || stuckId !== paintId || !urls.low) return null;
        shown = { low: urls.low, sharp: '' };
        probe = <img src={urls.low} alt="" hidden onError={() => setFailedId(paintId)} />;
    }

    const layerFit = objectFit || fit;
    const layerPosition = objectPosition || position;

    return (
        <div
            className={`relative min-h-0 min-w-0 ${className}`}
            style={{ width: '100%', height: '100%', ...box }}
            role="img"
            aria-label={alt}
        >
            {shown.low ? <div style={layerStyle(shown.low, layerFit, layerPosition)} /> : null}
            {shown.sharp && shown.sharp !== shown.low ? (
                <div style={layerStyle(shown.sharp, layerFit, layerPosition)} />
            ) : null}
            {probe}
        </div>
    );
}

HeldPicture.propTypes = {
    file: PropTypes.object,
    alt: PropTypes.string,
    fit: PropTypes.string,
    position: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    priority: PropTypes.bool,
};
